
import { Component, ProvideReactive, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { type Genre, type Genres } from '@/constants/types';
import { fetchConstants } from '@/graphql/apollo';
import {
	GetConstantsDocument,
	GetConstantsQuery,
	GetConstantsQueryVariables,
} from '@/graphql/queries/GetConstants.query';
import { pluck } from '@/helpers/object-helper';
import { ReadyType } from '@/stores/ready.store';

/** Use this symbol to Inject and Provide this constant. */
export const AgeCertificationsConstant = Symbol('AgeCertifications');
export const GenresConstant = Symbol('Genres');
export const GenresByShortNameConstant = Symbol('GenresByShortName');
/*********************/

const Language = namespace('language');

@Component({
	name: 'ConstantsProvider',
})
export default class ConstantsProvider extends Vue {
	@Language.Getter country: string;
	@Language.Getter language: string;

	@ProvideReactive(AgeCertificationsConstant)
	ageCertifications = {
		hasCertifications: false,
		movies: [] as Array<{ __typename?: 'AgeCertification'; technicalName: string; id: number }>,
		organizations: [] as string[],
		shows: [] as Array<{ __typename?: 'AgeCertification'; technicalName: string; id: number }>,
	};

	@ProvideReactive(GenresConstant)
	genres: Genre[] = [];

	@ProvideReactive(GenresByShortNameConstant)
	genresByShortName: Genres = {};

	async created() {
		if (process.server) {
			this.loadState();
		}
	}

	async mounted() {
		if (process.client) {
			this.loadState();
		}
	}

	async loadState() {
		const apolloClient = this.$apollo.getClient();

		await Vue.$jw.ready?.waitFor(ReadyType.CONSTANTS);
		// We read from the cache
		let constants;
		try {
			constants = apolloClient.readQuery<GetConstantsQuery, GetConstantsQueryVariables>({
				query: GetConstantsDocument,
				variables: { country: this.country, language: this.language },
			});
		} catch {
			// Constants missing: try to fetch again
			const { country, language } = this;
			const res = await fetchConstants({ country, language }, apolloClient, true);
			constants = res.data;
		}

		if (constants) {
			const { genres = [], organizations = [], movies = [], shows = [] } = constants;
			this.ageCertifications = {
				hasCertifications: movies.length > 0 || shows.length > 0,
				movies,
				organizations: [
					...new Set(
						organizations.map(({ organization }) => organization).filter((name: string) => name !== '')
					),
				],
				shows,
			};

			this.genres = genres;
			this.genresByShortName = pluck(this.genres, 'shortName');
		}
	}

	render() {
		if (this.$scopedSlots.default) {
			return this.$scopedSlots.default({});
		}
		return this.$slots[0];
	}
}
